import type { ApiSchemas } from '@rental/contracts/client'
import { customerName } from '@rental/domain'
import { countryName, format, formatDate, plural } from '@rental/i18n'
import { Button, buttonVariants, Notice, type Tone } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeft, Plus } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { BookingItem } from '../components/BookingItem'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { ListRow } from '../components/Results'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { Guard, RecordPending } from '../components/states'
import { EmailText, Facts, ProblemNotice } from '../components/ui'
import { type CorrectionOutcome, CustomerCorrectionForm } from '../customer/CustomerCorrectionForm'
import { AnonymisedNote, ErasedOn } from '../customer/Erased'
import { LicenceCorrectionForm } from '../customer/LicenceCorrectionForm'
import { PrivacyPanel } from '../customer/PrivacyPanel'
import { romeDay } from '../customer/privacy'
import { customerChanged, customerQueryKey, loadCustomer } from '../customer/queries'
import { FineMentions } from '../fines/FineMentions'
import { useLocale } from '../i18n'
import { problemOf, problemText, type StaffProblem } from '../problems'
import { useRomeToday } from '../rome-today'

const route = getRouteApi('/customers/$customerId')

type Detail = ApiSchemas['CustomerDetail']
type Customer = Detail['customer']

/**
 * One customer, for whoever reads customer records (UI-2g): anyone else who types its address reads
 * the refusal under its title, and nothing is asked of the server for it.
 */
export function CustomerScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'customers.read')} title={m.customer.title}>
      <CustomerRecord />
    </Guard>
  )
}

/**
 * CUS-1 and CUS-4: one customer, their licences and their rentals. S8: their
 * details put right by whoever holds customers.write, and the Owner's
 * privacy panel; an anonymised customer's record says what remains.
 */
function CustomerRecord() {
  const { messages: m } = useLocale()
  const { customerId } = route.useParams()
  const detail = useQuery({
    queryKey: customerQueryKey(customerId),
    queryFn: () => loadCustomer(customerId),
  })
  const problem = problemOf(detail.error)
  const back = (
    <Link to="/customers" className="flex min-h-touch items-center gap-1 self-start">
      <ChevronLeft aria-hidden="true" className="size-4" />
      {m.customer.back}
    </Link>
  )
  if (!detail.data) {
    return (
      <RecordPending
        back={back}
        title={m.customer.title}
        problem={problem}
        notFound={m.customer.notFound}
      />
    )
  }
  return <CustomerDetail detail={detail.data} back={back} problem={problem} />
}

function CustomerDetail({
  detail,
  back,
  problem,
}: {
  detail: Detail
  back: ReactNode
  /** A refresh that failed: said, while the customer as last read stays. */
  problem: StaffProblem | null
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const { customer } = detail
  const anonymised = customer.anonymisedAt !== null
  const requested = !anonymised && customer.erasureRequestedOn !== null
  const since = romeDay(locale, customer.createdAt)
  return (
    <>
      <PageHeader
        back={back}
        title={anonymised ? m.customer.anonymisedTitle : customerName(customer)}
        status={
          anonymised ? null : (
            <>
              {requested ? (
                <StatusChip tone="warning">{m.customer.erasureRequested}</StatusChip>
              ) : null}
              {customer.contactErasedAt !== null ? (
                <StatusChip tone="neutral">{m.customer.contactErased}</StatusChip>
              ) : null}
            </>
          )
        }
        actions={
          // S8: a record whose owner asked to be erased, or erased, takes no new booking.
          can(access, 'bookings.create') && !anonymised && !requested ? (
            <Link
              to="/bookings/new"
              search={{ customer: customer.id }}
              className={buttonVariants({ variant: 'staff' })}
            >
              <Plus aria-hidden="true" className="size-4" />
              {m.booking.forCustomer}
            </Link>
          ) : null
        }
        meta={
          customer.anonymisedAt !== null
            ? format(m.customer.anonymisedMeta, {
                since,
                date: romeDay(locale, customer.anonymisedAt),
              })
            : format(m.customer.since, { date: since })
        }
      >
        {requested && customer.erasureRequestedOn ? (
          <Notice tone="warning">
            <p>
              {format(m.customer.requestNotice, {
                date: formatDate(locale, customer.erasureRequestedOn, 'short'),
              })}
            </p>
          </Notice>
        ) : null}
      </PageHeader>
      <ProblemNotice problem={problem} />
      {customer.anonymisedAt !== null ? (
        <RemainsSection
          anonymisedAt={customer.anonymisedAt}
          vehiclePhotosDays={detail.retention.vehiclePhotosDays}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <DetailsSection customer={customer} />
          <LicencesSection customerId={customer.id} licences={detail.licences} />
        </div>
      )}
      <Section title={m.customer.history}>
        {detail.bookings.length === 0 ? (
          <p className="text-text-muted">{m.customer.noBookings}</p>
        ) : (
          <ul className="flex flex-col">
            {detail.bookings.map((row) => (
              <BookingItem key={row.code} row={row} kind="pickup" when="date-time" />
            ))}
          </ul>
        )}
      </Section>
      {/* CUS-4 (S6): the fines their rentals were matched to. */}
      {detail.fines.length > 0 ? (
        <Section title={m.fines.mentions.title}>
          <FineMentions fines={detail.fines} withCode />
        </Section>
      ) : null}
      {/* S8: the Owner's, and only the Owner's: nothing is asked of the server for anyone else. */}
      {can(access, 'customers.privacy') ? <PrivacyPanel customerId={customer.id} /> : null}
    </>
  )
}

/** What a correction ended in, in words for the outcome notice. */
function useOutcomeWords() {
  const { locale, messages: m } = useLocale()
  return (outcome: CorrectionOutcome, saved: string): { tone: Tone; text: string } => {
    switch (outcome.kind) {
      case 'saved':
        return { tone: 'success', text: format(saved, { fields: outcome.fields.join(', ') }) }
      case 'unchanged':
        return { tone: 'info', text: m.customer.correction.unchanged }
      case 'refused':
        return { tone: 'warning', text: problemText(outcome.problem, locale, m) }
      case 'gone':
        return { tone: 'warning', text: m.customer.licenceCorrection.gone }
    }
  }
}

/** An outcome said once per action: a new key each time, so each takes the focus (UI-2g). */
type Said = { key: number; tone: Tone; text: string }

/**
 * The customer's details, and "Modifica dati" for whoever holds
 * customers.write. A contact erased at the customer's request says so, with
 * its day, never "not given".
 */
function DetailsSection({ customer }: { customer: Customer }) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const queryClient = useQueryClient()
  const words = useOutcomeWords()
  const editId = useId()
  const [editing, setEditing] = useState(false)
  const [said, setSaid] = useState<Said | null>(null)
  const backToEdit = useRef(false)
  useEffect(() => {
    if (editing || !backToEdit.current) return
    backToEdit.current = false
    document.getElementById(editId)?.focus()
  }, [editing, editId])

  const notGiven = <span className="text-text-muted">{m.booking.notGiven}</span>
  const erased =
    customer.contactErasedAt === null ? null : <ErasedOn at={customer.contactErasedAt} />

  function done(outcome: CorrectionOutcome) {
    setEditing(false)
    setSaid({ key: Date.now(), ...words(outcome, m.customer.correction.saved) })
    void customerChanged(queryClient, customer.id)
  }

  return (
    <Section
      title={m.customer.contacts}
      actions={
        can(access, 'customers.write') && !editing ? (
          <Button
            id={editId}
            variant="outline"
            className="rounded-sm"
            onClick={() => {
              setSaid(null)
              setEditing(true)
            }}
          >
            {m.customer.correction.edit}
          </Button>
        ) : null
      }
    >
      {said ? (
        <OutcomeNotice key={said.key} tone={said.tone}>
          <p>{said.text}</p>
        </OutcomeNotice>
      ) : null}
      {editing ? (
        <CustomerCorrectionForm
          customer={customer}
          onDone={done}
          onCancel={() => {
            backToEdit.current = true
            setEditing(false)
          }}
        />
      ) : (
        <Facts
          items={[
            [
              m.booking.email,
              erased ??
                (customer.email ? (
                  <a key="email" href={`mailto:${customer.email}`} className="touch-target">
                    <EmailText address={customer.email} />
                  </a>
                ) : (
                  notGiven
                )),
            ],
            [
              m.booking.phone,
              erased ??
                (customer.phone ? (
                  <a key="phone" href={`tel:${customer.phone}`} className="figures touch-target">
                    {customer.phone}
                  </a>
                ) : (
                  notGiven
                )),
            ],
            [
              m.booking.birthDate,
              customer.birthDate ? formatDate(locale, customer.birthDate, 'short') : notGiven,
            ],
            [m.customer.birthPlace, customer.birthPlace ?? notGiven],
            [
              m.booking.nationality,
              customer.nationality ? countryName(locale, customer.nationality) : notGiven,
            ],
            [m.customer.address, customer.address ?? notGiven],
            [m.customer.language, m.languages[customer.preferredLocale]],
            [m.booking.notes, erased ?? customer.notes ?? notGiven],
          ]}
        />
      )}
    </Section>
  )
}

/** The licences on file, each put right on its own ("Modifica", customers.write). */
function LicencesSection({
  customerId,
  licences,
}: {
  customerId: string
  licences: Detail['licences']
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.customer.licenceCorrection
  const { access } = useAccess()
  const queryClient = useQueryClient()
  const words = useOutcomeWords()
  const editId = useId()
  const today = useRomeToday()
  const [editing, setEditing] = useState<string | null>(null)
  const [said, setSaid] = useState<Said | null>(null)
  const backToEdit = useRef<string | null>(null)
  useEffect(() => {
    if (editing !== null || backToEdit.current === null) return
    document.getElementById(`${editId}-${backToEdit.current}`)?.focus()
    backToEdit.current = null
  }, [editing, editId])
  const canCorrect = can(access, 'customers.write')

  function done(outcome: CorrectionOutcome) {
    setEditing(null)
    setSaid({ key: Date.now(), ...words(outcome, copy.saved) })
    void customerChanged(queryClient, customerId)
  }

  return (
    <Section title={m.customer.licences}>
      {said ? (
        <OutcomeNotice key={said.key} tone={said.tone}>
          <p>{said.text}</p>
        </OutcomeNotice>
      ) : null}
      {licences.length === 0 ? (
        <p className="text-text-muted">{m.customer.noLicences}</p>
      ) : (
        <ul className="flex flex-col">
          {licences.map((licence) => {
            if (editing === licence.id) {
              return (
                <li key={licence.id} className="py-3">
                  <LicenceCorrectionForm
                    customerId={customerId}
                    licence={licence}
                    onDone={done}
                    onCancel={() => {
                      backToEdit.current = licence.id
                      setEditing(null)
                    }}
                  />
                </li>
              )
            }
            // A fact about the date, no rule: whether it may still be driven on is the server's.
            // A warning, not red: a licence renewed since may be on record beside it.
            const expired = licence.expiresOn !== null && licence.expiresOn < today
            return (
              <ListRow
                key={licence.id}
                title={
                  <span className="figures font-medium">
                    {format(m.customer.licence, {
                      number: licence.number,
                      country: countryName(locale, licence.issuingCountry),
                    })}
                  </span>
                }
                end={
                  <>
                    {expired ? <StatusChip tone="warning">{m.customer.expired}</StatusChip> : null}
                    {canCorrect && editing === null ? (
                      <Button
                        id={`${editId}-${licence.id}`}
                        variant="outline"
                        className="rounded-sm"
                        aria-label={format(copy.editLabel, { number: licence.number })}
                        onClick={() => {
                          setSaid(null)
                          setEditing(licence.id)
                        }}
                      >
                        {copy.edit}
                      </Button>
                    ) : null}
                  </>
                }
              >
                <span>
                  {format(m.customer.categoriesHeld, {
                    categories: licence.categories
                      .map((held) =>
                        held.firstIssuedOn
                          ? `${held.code} (${formatDate(locale, held.firstIssuedOn, 'short')})`
                          : held.code,
                      )
                      .join(', '),
                  })}
                </span>
                {licence.expiresOn ? (
                  <span>
                    {format(expired ? m.customer.expiredOn : m.customer.expires, {
                      date: formatDate(locale, licence.expiresOn, 'short'),
                    })}
                  </span>
                ) : null}
                {licence.hasIdp ? <span>{m.customer.idp}</span> : null}
              </ListRow>
            )
          })}
        </ul>
      )}
    </Section>
  )
}

/**
 * S8: an anonymised customer's record names nobody. It says what went and
 * when, what the law keeps and for how long, and who may still open what
 * names them.
 */
function RemainsSection({
  anonymisedAt,
  vehiclePhotosDays,
}: {
  anonymisedAt: string
  vehiclePhotosDays: number | null
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.customer.remains
  return (
    <Section title={copy.title}>
      <AnonymisedNote anonymisedAt={anonymisedAt} />
      <p>
        {vehiclePhotosDays === null
          ? copy.vehiclePhotosKept
          : plural(locale, copy.vehiclePhotos, vehiclePhotosDays)}
      </p>
    </Section>
  )
}
